import { useMemo } from 'react';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import InputAdornment from '@mui/material/InputAdornment';
import Typography from '@mui/material/Typography';
import PropTypes from 'prop-types';
import { useForm, useFormState } from 'react-hook-form';

import SubmitButton from 'components/buttons/SubmitButton';
import AlertDialog from 'components/dialogs/AlertDialog';
import PageTitle from 'components/layout/PageTitle';
import RadioButtonsGroup from 'components/radio-buttons/RadioButtonsGroup';
import FormTextField from 'components/text-fields/FormTextField';
import useGetFilters from 'custom-hooks/useGetFilters';
import { POSITIVE_NUMBER, REQUIRED_FIELD } from 'lib/form';
import {
  DEFAULT_LINEAL_UNIT_MEASURE,
  DEFAULT_WEIGHT_UNIT_MEASURE,
} from 'lib/productUnitMeasures';

import useDialog from '../hooks/useDialog';
import useError from '../hooks/useError';
import useSubmit from '../hooks/useSubmit';
import FORM_TYPES from '../lib/formTypes';
import {
  PACK_TYPE_OPTIONS,
  PACK_TYPE_DYNAMIC_VALUE,
  PACK_TYPE_FIXED_VALUE,
} from '../lib/packTypes';
import PackComponentsForm from '../new/components/PackComponentsForm';

const INPUT_TYPE_NUMBER = 'number';

const MEASURES_MIN = 0;
const UPDATE_PACK_BUTTON_NAME = 'ACTUALIZAR PACK';
const CREATE_PACK_BUTTON_NAME = 'CREAR PACK';

function PackForm({ formType, defaultValues }) {
  const { filterOptions: formOptions } = useGetFilters({
    filterOptionsKey: 'catalogFilterOptions',
  });

  const { register, control, handleSubmit, watch, reset } = useForm({
    defaultValues,
  });
  const watchSellerId = watch('sellerId');
  const { dirtyFields } = useFormState({ control });

  const resetForm = () => {
    reset(defaultValues);
  };

  const { dialog } = useDialog({ formType, resetForm });
  const { loading, error, onSubmit } = useSubmit({
    setDialogContent: dialog.setContent,
    setVisibleDialog: dialog.setOpen,
    formType,
  });

  const errorMessage = useError(error);

  const disabledSubmit = useMemo(() => {
    if (formType === FORM_TYPES.new) {
      return loading;
    }
    return !Object.keys(dirtyFields).length || loading;
  });
  return (
    <Container maxWidth="md">
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          padding: 2,
          flexDirection: 'column',
        }}
      >
        {formType === FORM_TYPES.new ? (
          <PageTitle>Crear pack</PageTitle>
        ) : (
          <PageTitle>Editar pack</PageTitle>
        )}
        <Typography variant="subtitle1">1. Vendedor</Typography>
        <FormTextField
          fieldName="sellerId"
          label="Vendedor"
          control={control}
          register={() => register('sellerId', { required: REQUIRED_FIELD })}
          select={{
            select: true,
            options: formOptions.sellers,
          }}
          disabled={formType === FORM_TYPES.edit}
        />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <Typography variant="subtitle1">2. Datos generales</Typography>
          <FormTextField
            fieldName="productName"
            control={control}
            label="Nombre del producto"
            sx={{ width: '100%' }}
            register={() =>
              register('productName', { required: REQUIRED_FIELD })
            }
          />
          <FormTextField
            fieldName="sku"
            control={control}
            label="SKU"
            sx={{ width: '100%' }}
            register={() => register('sku', { required: REQUIRED_FIELD })}
            disabled={formType === FORM_TYPES.edit}
          />
          <RadioButtonsGroup
            label="Tipo de pack"
            options={PACK_TYPE_OPTIONS}
            control={control}
            fieldName="packType"
          />

          <Typography variant="subtitle1">3. Selección de productos</Typography>
          {!watchSellerId && formType === FORM_TYPES.new && (
            <Alert severity="warning">
              Debes seleccionar primero al vendedor (sección 1) para poder
              agregar productos
            </Alert>
          )}
          <PackComponentsForm
            control={control}
            register={register}
            watchSellerId={
              formType === FORM_TYPES.new
                ? watchSellerId
                : defaultValues.sellerId
            }
          />
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <Typography variant="subtitle1">4. Medidas</Typography>
          <Typography variant="subtitle3">Medidas lineales</Typography>
          <FormTextField
            fieldName="measures.height.value"
            control={control}
            label="Alto"
            sx={{ width: '100%' }}
            register={() =>
              register('measures.height.value', {
                required: REQUIRED_FIELD,
                valueAsNumber: true,
                min: {
                  value: MEASURES_MIN,
                  message: POSITIVE_NUMBER,
                },
              })
            }
            type={INPUT_TYPE_NUMBER}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {defaultValues.measures?.height?.unit ||
                    DEFAULT_LINEAL_UNIT_MEASURE}
                </InputAdornment>
              ),
            }}
          />
          <FormTextField
            fieldName="measures.width.value"
            control={control}
            label="Ancho"
            sx={{ width: '100%' }}
            register={() =>
              register('measures.width.value', {
                required: REQUIRED_FIELD,
                valueAsNumber: true,
                min: {
                  value: MEASURES_MIN,
                  message: POSITIVE_NUMBER,
                },
              })
            }
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {defaultValues.measures?.width?.unit ||
                    DEFAULT_LINEAL_UNIT_MEASURE}
                </InputAdornment>
              ),
            }}
            type={INPUT_TYPE_NUMBER}
          />
          <FormTextField
            fieldName="measures.length.value"
            control={control}
            label="Largo"
            sx={{ width: '100%' }}
            register={() =>
              register('measures.length.value', {
                required: REQUIRED_FIELD,
                valueAsNumber: true,
                min: {
                  value: MEASURES_MIN,
                  message: POSITIVE_NUMBER,
                },
              })
            }
            type={INPUT_TYPE_NUMBER}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {defaultValues.measures?.length?.unit ||
                    DEFAULT_LINEAL_UNIT_MEASURE}
                </InputAdornment>
              ),
            }}
          />
          <Typography variant="subtitle3">Medidas de peso</Typography>
          <FormTextField
            fieldName="measures.weight.value"
            control={control}
            label="Peso"
            sx={{ width: '100%' }}
            register={() =>
              register('measures.weight.value', {
                required: REQUIRED_FIELD,
                valueAsNumber: true,
                min: {
                  value: MEASURES_MIN,
                  message: POSITIVE_NUMBER,
                },
              })
            }
            type={INPUT_TYPE_NUMBER}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {defaultValues.measures?.weight?.unit ||
                    DEFAULT_WEIGHT_UNIT_MEASURE}
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            mt: 2,
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {!!error && (
              <Typography color="error" align="center" gutterBottom>
                {errorMessage}
              </Typography>
            )}
            <SubmitButton
              size="large"
              variant="contained"
              onClick={handleSubmit(onSubmit)}
              disabled={disabledSubmit}
              loading={loading}
              label={
                formType === FORM_TYPES.edit
                  ? UPDATE_PACK_BUTTON_NAME
                  : CREATE_PACK_BUTTON_NAME
              }
            />
          </Box>
        </Box>
        <AlertDialog
          open={dialog.open}
          handleClose={dialog.handleClose}
          dialogTitle={dialog.title}
          dialogContent={dialog.content}
        />
      </Box>
    </Container>
  );
}

PackForm.propTypes = {
  formType: PropTypes.oneOf(['edit', 'new']).isRequired,
  defaultValues: PropTypes.shape({
    sellerId: PropTypes.string,
    productName: PropTypes.string,
    sku: PropTypes.string,
    height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    length: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    weight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    measures: PropTypes.shape({
      width: PropTypes.shape({
        value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        unit: PropTypes.string,
      }),
      height: PropTypes.shape({
        value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        unit: PropTypes.string,
      }),
      length: PropTypes.shape({
        value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        unit: PropTypes.string,
      }),
      weight: PropTypes.shape({
        value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        unit: PropTypes.string,
      }),
    }),
    components: PropTypes.arrayOf(PropTypes.shape({})),
    packType: PropTypes.oneOf([
      PACK_TYPE_FIXED_VALUE,
      PACK_TYPE_DYNAMIC_VALUE,
      '',
    ]),
  }),
};
PackForm.defaultProps = {
  defaultValues: {
    sellerId: '',
    productName: '',
    sku: '',
    measures: {
      width: { value: '', unit: DEFAULT_LINEAL_UNIT_MEASURE },
      height: { value: '', unit: DEFAULT_LINEAL_UNIT_MEASURE },
      length: { value: '', unit: DEFAULT_LINEAL_UNIT_MEASURE },
      weight: { value: '', unit: DEFAULT_WEIGHT_UNIT_MEASURE },
    },
    components: [],
    packType: '',
  },
};
export default PackForm;
